import * as angular from "angular";
import * as _ from "lodash";
import {BringozToastService} from "../../commons/services/bringoz-toast.service";
import {PlanningRouteModel} from "../models/planning-route.model";

export interface JobRouteValidationOptions {
  checkOptimizing?: boolean;
  isAllowDraggingIntoDispatchedRoute?: boolean;
}

export class PlanningAlertsService {

  constructor(private bringozToastService: BringozToastService, private $translate) {
  }

  getJobValidationError(job, route, options?: JobRouteValidationOptions): string | null {
    if (_.includes(route.jobIds, job.id)) {
      return 'alreadyInRoute';
    }
    if (!!route.locked) {
      return 'routeLocked';
    }
    if (options?.checkOptimizing) {
      const isRunning = route.actionProcess?.running && (new Date().getTime() < route.actionProcess?.expirationDate);
      if (!!isRunning) {
        return 'routeOptimizing';
      }
    }
    if (route.status === 'DISPATCHED' && !options?.isAllowDraggingIntoDispatchedRoute) {
      return 'routeDispatched';
    }

    return null;
  }

  private _jobAssignErrorAlert(errorType: string) {
    switch (errorType) {
      case 'alreadyInRoute':
        this.alreadyInRouteInfo();
        break;
      case 'routeLocked':
        this.routeIsLocked();
        break;
      case 'routeOptimizing':
        this.optimizingDraftError();
        break;
      case 'routeDispatched':
        this.routeIsDispatchedError();
        break;
    }
  }

  getProjectsError() {
    const message = {
      body: 'PLANNING_PROJECTS_GET_ALL_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // CREATE //
  createProjectSuccess(project?, enableView?) {
    const message = {
      body: 'PLANNING_PROJECTS_CREATE_SUCCESS'
    };
    const view = enableView ? 'View' : null;
    return this.bringozToastService.success(message, project, view)
  }

  createProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_CREATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  changeRouteResourceSuccess() {
    const message = {
      body: 'PLANNING_UPDATE_RESOURCE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  changeRouteResourceError() {
    const message = {
      body: 'PLANNING_UPDATE_RESOURCE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE //
  updateProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE //
  deleteProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }


  // CREATE //
  createTemplateSuccess() {
    const message = {
      body: 'PLANNING_TEMPLATE_CREATE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  createTemplateError() {
    const message = {
      body: 'PLANNING_TEMPLATE_CREATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE //
  updateTemplateSuccess() {
    const message = {
      body: 'PLANNING_TEMPLATE_UPDATE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateTemplateError() {
    const message = {
      body: 'PLANNING_TEMPLATE_UPDATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE //
  deleteTemplateSuccess() {
    const message = {
      body: 'PLANNING_TEMPLATE_DELETE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteTemplateError() {
    const message = {
      body: 'PLANNING_TEMPLATE_DELETE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // GET_PROJECT_JOBS //
  getProjectJobsError() {
    const message = {
      body: 'PLANNING_GET_PROJECT_JOBS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // GET_PROJECT_RESOURCES //
  getProjectResourcesError() {
    const message = {
      body: 'PLANNING_GET_PROJECT_RESOURCES_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // GET_PROJECT_DEPOTS //
  getProjectDepotsError() {
    const message = {
      body: 'PLANNING_GET_PROJECT_DEPOTS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }


  // ADD JOB TO PROJECT //
  addJobToProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_JOB_TO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  addJobToProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_JOB_TO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ADD ORDERS TO PROJECT //
  addOrdersToProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_ORDERS_TO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  addOrdersToProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_ORDERS_TO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ADD SERVICES TO PROJECT //
  addStepsToProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_STEPS_TO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  addStepsToProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_STEPS_TO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ADD RESOURCES TO PROJECT //
  addResourcesToProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_RESOURCES_TO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  addResourcesToProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_RESOURCES_TO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ADD DEPOTS TO PROJECT //
  addDepotsToProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_DEPOTS_TO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  addDepotsToProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_ADD_DEPOTS_TO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE RESOURCES FROM PROJECT //
  deleteResourcesFromProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_RESOURCES_FROM_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteResourcesFromProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_RESOURCES_FROM_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE DEPOTS FROM PROJECT //
  deleteDepotsFromProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_DEPOTS_FROM_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteDepotsFromProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_DEPOTS_FROM_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE JOB IN PROJECT //
  updateJobInProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_JOB_IN_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateJobInProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_JOB_IN_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE_JOB_IN_DRAFT //
  updateJobInDraftSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_JOB_IN_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateJobInDraftError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_JOB_IN_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE_RESOURCE_IN_DRAFT //
  updateResourcesInDraftSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_RESOURCE_IN_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateResourcesInDraftError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_RESOURCE_IN_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE JOB FROM PROJECT //
  deleteJobFromProjectSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_JOB_FROM_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteJobFromProjectError() {
    const message = {
      body: 'PLANNING_PROJECTS_DELETE_JOB_FROM_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE PROJECT DETAILS //
  updateProjectDetailsSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_PROJECT_DETAILS_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateProjectDetailsError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_PROJECT_DETAILS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE PROJECT SETTINGS //
  updateProjectSettingsSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_PROJECT_SETTINGS_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateProjectSettingsError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_PROJECT_SETTINGS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE DRAFT SETTINGS //
  updateDraftSettingsSuccess() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_DRAFT_SETTINGS_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateDraftSettingsError() {
    const message = {
      body: 'PLANNING_PROJECTS_UPDATE_DRAFT_SETTINGS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // CREATE DRAFT IN PROJECT //
  createDraftInProjectSuccess() {
    const message = {
      body: 'PLANNING_CREATE_DRAFT_IN_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  createDraftInProjectError() {
    const message = {
      body: 'PLANNING_CREATE_DRAFT_IN_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE DRAFT FROM PROJECT //
  deleteDraftsFromProjectSuccess() {
    const message = {
      body: 'PLANNING_DELETE_DRAFT_FROM_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteDraftsFromProjectError() {
    const message = {
      body: 'PLANNING_DELETE_DRAFT_FROM_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // NO_ROUTES_TO_DISPATCH //
  noRoutesToDispatchInfo() {
    const message = {
      body: 'PLANNING_DRAFT_NO_ROUTES_TO_DISPATCH_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // RE OPTIMIZE DRAFT //
  optimizingDraftInfo(draft) {
    const message = {
      body: 'X_PLANNING_OPTIMIZE_DRAFT_INFO'
    };
    return this.bringozToastService.success(message, draft)
  }

  optimizingDraftError() {
    const message = {
      body: 'X_PLANNING_OPTIMIZE_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ABORT OPTIMIZE DRAFT //
  abortOptimizingDraftSuccess(draft?) {
    const message = {
      body: 'PLANNING_ABORT_OPTIMIZE_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message, draft)
  }

  abortOptimizingDraftError() {
    const message = {
      body: 'PLANNING_ABORT_OPTIMIZE_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // APPROVE AND DISPATCH DRAFT //
  approveAndDispatchDraftSuccess() {
    const message = {
      body: 'PLANNING_APPROVE_AND_DISPATCH_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  approveAndDispatchDraftError() {
    const message = {
      body: 'PLANNING_APPROVE_AND_DISPATCH_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  draftCannotBeDispatchedError() {
    const message = {
      body: 'PLANNING_DRAFT_CANNOT_BE_DISPATCHED_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // RECALC_ROUTE //
  recalcRouteSuccess() {
    const message = {
      body: 'PLANNING_RECALC_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  recalcRouteError() {
    const message = {
      body: 'PLANNING_RECALC_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // DELETE_ROUTE //
  deleteRouteSuccess() {
    const message = {
      body: 'PLANNING_DELETE_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  deleteRouteError() {
    const message = {
      body: 'PLANNING_DELETE_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // LOCK_ROUTE //
  lockRouteSuccess() {
    const message = {
      body: 'PLANNING_LOCK_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  lockRouteError() {
    const message = {
      body: 'PLANNING_LOCK_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UNLOCK_ROUTE //
  unlockRouteSuccess() {
    const message = {
      body: 'PLANNING_UNLOCK_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  unlockRouteError() {
    const message = {
      body: 'PLANNING_UNLOCK_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // LOCK_JOB //
  lockJobSuccess() {
    const message = {
      body: 'JOB_LOCK_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  lockJobError() {
    const message = {
      body: 'JOB_LOCK_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UNLOCK_JOB //
  unlockJobSuccess() {
    const message = {
      body: 'JOB_UNLOCK_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  unlockJobError() {
    const message = {
      body: 'JOB_UNLOCK_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  removeLockedJobInfo() {
    const message = {
      body: 'PLANNING_REMOVE_LOCKED_JOB_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // CREATE_ROUTE_IN_DRAFT //
  createRouteInDraftSuccess() {
    const message = {
      body: 'PLANNING_CREATE_ROUTE_IN_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  createRouteInDraftError() {
    const message = {
      body: 'PLANNING_CREATE_ROUTE_IN_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE_ROUTE //
  updateRouteSuccess() {
    const message = {
      body: 'PLANNING_UPDATE_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateRouteError() {
    const message = {
      body: 'PLANNING_UPDATE_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // UPDATE_ROUTE //
  updateRoutesSuccess() {
    const message = {
      body: 'PLANNING_UPDATE_ROUTES_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  updateRoutesError() {
    const message = {
      body: 'PLANNING_UPDATE_ROUTES_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // ACTION_RECEIVED //
  actionReceivedSuccess() {
    const message = {
      body: 'PLANNING_ACTION_RECEIVED_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  actionReceivedError() {
    const message = {
      body: 'PLANNING_ACTION_RECEIVED_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  routeIndexOutOfBoundsError() {
    const message = {
      body: 'PLANNING_ROUTE_INDEX_OUT_OF_BOUNDS_ERROR',
    };
    return this.bringozToastService.error(message);
  }

  routeIndexOrderInvalidError() {
    const message = {
      body: 'PLANNING_ROUTE_INDEX_ORDER_INVALID_ERROR',
    };
    return this.bringozToastService.error(message);
  }

  jobAlreadyOnRouteError() {
    const message = {
      body: 'PLANNING_JOB_ALREADY_ON_ROUTE_ERROR',
    };
    return this.bringozToastService.error(message);
  }

  noMatchingPickupLocationError() {
    const message = {
      body: 'PLANNING_NO_MATCHING_PICKUP_LOCATION_ERROR',
    };
    return this.bringozToastService.error(message);
  }

  noMatchingDropoffLocationError() {
    const message = {
      body: 'PLANNING_NO_MATCHING_DROPOFF_LOCATION_ERROR',
    };
    return this.bringozToastService.error(message);
  }

  // CREATE DRAFTS NO STOPS INFO //
  createDraftNoStopsInfo() {
    const message = {
      body: 'PLANNING_CREATE_DRAFTS_NO_STOPS_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // CREATE DRAFTS NO RESOURCES INFO //
  createDraftNoResourcesInfo() {
    const message = {
      body: 'PLANNING_CREATE_DRAFTS_NO_RESOURCES_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // GET RESOURCES //
  getResourcesError() {
    const message = {
      body: 'PLANNING_GET_RESOURCES_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // GET DEPOTS //
  getDepotsError() {
    const message = {
      body: 'PLANNING_GET_DEPOTS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  // NO AVAILABLE RESOURCES //
  noAvailableResourcesInfo() {
    const message = {
      body: 'PLANNING_NO_AVAILABLE_RESOURCES_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // NO AVAILABLE RESOURCES //
  noAvailableRoutesInfo() {
    const message = {
      body: 'PLANNING_NO_AVAILABLE_ROUTES_INFO'
    };
    return this.bringozToastService.info(message)
  }

  // STOP_TYPE_TO_ROUTE_REJECTION//
  stopTypeToRouteRejectionInfo(stopType, routeType) {
    const message = {
      body: 'X_PLANNING_STOP_TYPE_TO_ROUTE_REJECTION_INFO'
    };
    return this.bringozToastService.info(message, {stopType: stopType, routeType: routeType})
  }

  // EXPORT_DRAFT //
  exportDraftSuccess() {
    const message = {
      body: 'PLANNING_EXPORT_DRAFT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  exportDraftError() {
    const message = {
      body: 'PLANNING_EXPORT_DRAFT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  exportRouteSuccess() {
    const message = {
      body: 'PLANNING_EXPORT_ROUTE_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  exportRouteError() {
    const message = {
      body: 'PLANNING_EXPORT_ROUTE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  routeIsLocked() {
    const message = {
      body: 'PLANNING_ROUTE_IS_LOCKED_INFO'
    };
    return this.bringozToastService.info(message)
  }

  routeIsDispatchedError() {
    const message = {
      body: 'PLANNING_ROUTE_IS_DISPATCHED_INFO'
    };
    return this.bringozToastService.error(message)
  }

  routeIsDispatchedOrPartialDispatchedInfo() {
    const message = {
      body: 'PLANNING_ROUTE_IS_DISPATCHED_PARTIAL_DISPATCHED_INFO'
    };
    return this.bringozToastService.info(message)
  }

  noMatchingSkillsInfo() {
    const message = {
      body: 'RESOURCES_NO_MATCHING_SKILLS_INFO'
    };
    return this.bringozToastService.info(message)
  }

  alreadyInRouteInfo() {
    const message = {
      body: 'PLANNING_ALREADY_IN_ROUTE_INFO'
    };
    return this.bringozToastService.info(message)
  }

  resourceAlreadyInUse() {
    const message = {
      body: 'PLANNING_RESOURCE_ALREADY_IN_USE_INFO'
    };
    return this.bringozToastService.info(message)
  }

  resourceNotAllowingRetripsInfo() {
    const message = {
      body: 'PLANNING_RESOURCE_NOT_ALLOWING_RETRIPS_INFO'
    };
    return this.bringozToastService.info(message)
  }

  importJobsIntoProjectSuccess() {
    const message = {
      body: 'IMPORT_JOBS_INTO_PROJECT_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  importJobsIntoProjectError() {
    const message = {
      body: 'IMPORT_JOBS_INTO_PROJECT_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  createDefaultDraftTemplateSuccess() {
    const message = {
      body: 'PLANNING_DEFAULT_DRAFT_TEMPLATE_CREATE_SUCCESS',
    };
    return this.bringozToastService.success(message)
  }

  createDefaultDraftTemplateError() {
    const message = {
      body: 'PLANNING_DEFAULT_DRAFT_TEMPLATE_CREATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  updateDefaultDraftTemplateSuccess() {
    const message = {
      body: 'PLANNING_DEFAULT_DRAFT_TEMPLATE_UPDATE_SUCCESS',
    };
    return this.bringozToastService.success(message)
  }

  updateDefaultDraftTemplateError() {
    const message = {
      body: 'PLANNING_DEFAULT_DRAFT_TEMPLATE_UPDATE_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  jobSplitSuccess() {
    const message = {
      body: 'SPLIT_JOB_SUCCESS_BY_PACKAGE_DETAILS',
      details: 'SPLIT_JOB_SUCCESS'
    }
    return this.bringozToastService.success(message)
  }

  jobsSplitRevertByPackagesSuccess() {
    const message = {
      body: 'SPLIT_JOB_REVERT_PACKAGES_SUCCESS',
      details: 'SPLIT_JOB_REVERT_PACKAGES_SUCCESS_DETAILS'
    }
    return this.bringozToastService.success(message)
  }

  jobsSplitRevertByLocationSuccess() {
    const message = {
      body: 'SPLIT_JOB_REVERT_LOCATION_SUCCESS',
      details: 'SPLIT_JOB_REVERT_LOCATION_SUCCESS_DETAIL'
    }
    return this.bringozToastService.success(message)
  }

  // True when the split-undo error was recognized and handled, false otherwise (caller should fall
  // back to a generic error). 400491 = SPLIT_UNDO_BLOCKED_BY_STEP: the order changed the split's
  // structure, so undoing it would bring back work the order dropped — no retry.
  handleJobSplitUndoError(error): boolean {
    const code = _.get(error, 'error.code') || _.get(error, 'code');
    if (code !== 400491) {
      return false;
    }
    this.bringozToastService.error({body: 'PLANNING_DRAFT_JOB_SPLIT_UNDO_BLOCKED_BY_STEP'});
    return true;
  }

  generateJobSplitSuggestionsError() {
    const message = {
      body: "GENERATE_JOB_SPLIT_SUGGESTIONS_FAILED",
      details: 'SOMETHING_WENT_WRONG'
    }
    return this.bringozToastService.error(message);
  }

  bulkJobSplitFailed() {
    const message = {
      body: "BULK_JOB_SPLIT_FAILED",
      details: 'SOMETHING_WENT_WRONG'
    }
    return this.bringozToastService.error(message);
  }

  bulkJobSplitSuccess() {
    const message = {
      body: 'BULK_SPLIT_JOB_SUCCESS',
    }
    return this.bringozToastService.success(message)
  }

  bulkDropJobsError(errorCounts: Record<string, number>) {
    const errorReasonBodyMap = {
      alreadyInRoute: 'PLANNING_ALREADY_IN_ROUTE_INFO',
      routeLocked: 'PLANNING_ROUTE_IS_LOCKED_INFO',
      routeOptimizing: 'PLANNING_ROUTE_IS_LOCKED_INFO',
      routeDispatched: 'PLANNING_ROUTE_IS_DISPATCHED_INFO'
    };
    const reasons = Object.keys(errorCounts);

    if (reasons.length === 1) {
      this._jobAssignErrorAlert(reasons[0]);
      return;
    }

    const totalFailed = reasons.reduce((sum, key) => sum + errorCounts[key], 0);

    const description = reasons
      .sort((a, b) => errorCounts[b] - errorCounts[a])
      .map((reason) => {
        const translatedReason = this.$translate.instant('alerts.' + errorReasonBodyMap[reason]);
        return errorCounts[reason] + ' - ' + translatedReason;
      })
      .join(', ');

    const message = {
      details: 'PLANNING_BULK_DROP_JOBS_ERROR',
      body: 'PLANNING_BULK_DROP_JOBS_ERROR_DESCRIPTION'
    };
    return this.bringozToastService.info(message, {failed: totalFailed, description: description});
  }

  bulkDropJobsSuccess(count: number) {
    const message = {
      body: 'PLANNING_BULK_DROP_JOBS_SUCCESS'
    };
    return this.bringozToastService.success(message, {count: count});
  }

  invalidTaskOrder() {
    const message = {
      body: 'ROUTE_INVALID_TASK_ORDER',
    };
    return this.bringozToastService.error(message)
  }

  // DRAFT JOB CHANGES //
  scanDraftJobChangesError() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_SCAN_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  applyDraftJobChangesSuccess() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_APPLY_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  applyDraftJobChangesError() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_APPLY_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  dismissDraftJobChangesSuccess() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_DISMISS_SUCCESS'
    };
    return this.bringozToastService.success(message)
  }

  dismissDraftJobChangesError() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_DISMISS_ERROR',
      details: 'SOMETHING_WENT_WRONG'
    };
    return this.bringozToastService.error(message)
  }

  draftJobChangesNoJobsForProcessing() {
    const message = {
      body: 'PLANNING_DRAFT_JOB_CHANGES_NO_JOBS_FOR_PROCESSING'
    };
    return this.bringozToastService.info(message)
  }

  // Dispatches the four jobChanges error codes to the right toast. Returns true if the code was
  // recognized and handled, false otherwise (caller should fall back to a generic error).
  // Renumbered 400479-482 -> 400481-484 in the v2 changelist (the 400479/400480 slots were taken
  // on develop) — names/meanings are unchanged, only the numeric codes shifted by 2.
  handleDraftJobChangesError(error): boolean {
    const code = _.get(error, 'error.code');
    switch (code) {
      case 400483: {
        const message = {body: 'PLANNING_DRAFT_JOB_CHANGES_NOT_ALLOWED_IN_STATUS'};
        this.bringozToastService.error(message);
        return true;
      }
      case 400484: {
        const message = {body: 'PLANNING_DRAFT_JOB_CHANGES_SYNC_IN_PROGRESS'};
        this.bringozToastService.info(message);
        return true;
      }
      case 400485:
        // Not a real failure — the draft still moved to a terminal state server-side.
        this.draftJobChangesNoJobsForProcessing();
        return true;
      case 400486: {
        const message = {body: 'PLANNING_DRAFT_JOB_CHANGES_DISABLED'};
        this.bringozToastService.error(message);
        return true;
      }
      default:
        return false;
    }
  }
}

angular
  .module('bringoz.admin.planning')
  .service('planningAlertsService', PlanningAlertsService);
